/**
 * Identidad visual de una tienda: iniciales sobre un color estable.
 *
 * Vive aparte porque ya se dibuja en DOS lugares —el selector de tiendas del topbar y la cabecera
 * del menú en móvil— y el color TIENE que ser el mismo en ambos: es el patrón de Shopify, donde
 * cada tienda conserva su color entre sesiones y pantallas. Duplicar la paleta habría hecho que
 * una tienda cambiara de color según dónde se la mire, que es peor que no tener color.
 */

/**
 * Genera un color determinístico desde un string (tenant_id).
 *
 * Paleta limitada (10 colores) por consistencia visual. Hash simple sobre el string para mapear
 * a uno.
 *
 * 🪤 CADA COLOR ES EL MÁS VIVO DE SU FAMILIA QUE SE LEE CON LETRA BLANCA (4,5 : 1 o más). Los de antes
 *    (los 500 de Tailwind) daban entre 2 y 4,5 : 1 —el lima 1,98—, y la piel les ponía encima un velo negro
 *    parejo del 42 % que los dejaba en 6,5–9,6 : 1: el rojo salía granate, el rosa vino y el lima oliva
 *    (founder, 21-oct: «los logos de las tiendas se ven oscurecidos»). Mismo orden y misma familia que
 *    antes, así cada tienda conserva su color; `data-color-legible` le dice a la piel que no lo vele.
 */
function colorFromId(id: string): string {
  const palette = [
    '#047857', // verde (default) · 5,5 : 1
    '#7C3AED', // púrpura · 5,7
    '#2563EB', // azul · 5,2
    '#B45309', // ámbar · 5,0
    '#DC2626', // rojo · 4,8
    '#DB2777', // rosa · 4,6
    '#0F766E', // teal · 5,5
    '#C2410C', // naranja · 5,2
    '#4F46E5', // índigo · 6,3
    '#4D7C0F', // lima · 5,0
  ];
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return palette[hash % palette.length];
}

/** Extrae 2 letras (mayúsculas) del nombre. "My Store 2" → "MS", "Rebeldia" → "RE". */
function initialsOf(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '??';
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

/** Avatar circular con iniciales sobre fondo de color. */
export function TenantAvatar({ id, name, size = 24 }: { id: string; name: string; size?: number }) {
  const bg = colorFromId(id);
  const fontSize = Math.round(size * 0.45);
  return (
    <span
      aria-hidden
      data-color-legible
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: bg,
        color: '#fff',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize,
        fontWeight: 600,
        flexShrink: 0,
        letterSpacing: '-0.5px',
      }}
    >
      {initialsOf(name)}
    </span>
  );
}
